import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pagination } from '@thinknimble/tn-models'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import {
  ChoiceOptionFilter,
  completeSurveyInstance,
  createSurveyResponse,
  getPreviousQuestion,
  getSurveyInstance,
  SurveyInstance,
  surveyQuestionCOapi,
  SurveyResponseCreate,
  SurveyResponseUpdate,
  updateSurveyResponse,
  createSurveyInstance as createSurveyInstanceApi,
} from 'src/services/survey'
import { SurveyContext } from './survey-provider'

const surveyTemplateByLanguage: Record<string, string> = {
  en: 'a9535927-27ed-4a9e-89f3-b01e0c70ee23',
  es: '0a999a57-56a1-4ce8-a98f-e075665826be',
}

export function useSurvey(onFinishAction?: () => void, language: string = 'en') {
  const {
    setResponseRef,
    response,
    setResponse,
    instance,
    setInstance,
    instanceId,
    setInstanceId,
    setCurrentSection,
  } = useContext(SurveyContext)
  const templateId = surveyTemplateByLanguage[language] ?? surveyTemplateByLanguage.en
  // TODO see if better router
  const navigate = useNavigate()
  const location = useLocation()
  const [isFinished, setIsFinished] = useState<boolean>(false)
  const [errors, setErrors] = useState<string[]>([])

  const [loading, setLoading] = useState<boolean>(false)

  const [responseId, setResponseId] = useState<string | null | undefined>(null)
  const qc = useQueryClient()
  const currentQuestion = useMemo(() => {
    return instance?.cursorRef?.currentQuestionRef
  }, [instance])

  const isLast = useMemo(() => {
    return instance?.templateRef?.lastQuestion === currentQuestion?.id
  }, [currentQuestion, instance])

  const isSurveyInitialized = useRef(false)

  const handleResponseFormat = useCallback(
    (value: any) => {
      if (currentQuestion?.questionType === 'MULTIPLE_OPTION' && !value) {
        setResponse([])
      } else if (currentQuestion?.questionType === 'MULTIPLE_OPTION' && !Array.isArray(value)) {
        setResponse([value])
      } else {
        setResponse(value)
      }
      return value
    },
    [currentQuestion?.questionType, setResponse],
  )

  const { mutate: createSurveyInstance } = useMutation({
    mutationFn: async (_data: { reservationId?: string }) => {
      return await createSurveyInstanceApi({
        template: templateId,
      })
    },
    onMutate: () => setLoading(true),
    onSuccess: async (data: SurveyInstance, variables) => {
      setInstance(data as SurveyInstance)
      setInstanceId(data.id)
      isSurveyInitialized.current = true
      if (setCurrentSection) {
        setCurrentSection(0)
      }

      if (variables.reservationId) {
        // Keep the query (e.g. `?escalate=`) so the finish step knows its target.
        navigate(
          {
            pathname: `/reservations/${variables.reservationId}/guests/${data.id}`,
            search: location.search,
          },
          { replace: true },
        )
      }
    },
    onSettled: () => setLoading(false),
  })

  const { data: existingInstance, isLoading: loadingExistingInstance } = useQuery({
    queryKey: ['instance', instanceId],
    queryFn: async () => await getSurveyInstance(instanceId ?? ''),
    enabled: !!instanceId && instance?.id !== instanceId,
    refetchOnMount: 'always',
  })

  const { mutate: saveAndNext } = useMutation({
    mutationFn: async (data: SurveyResponseCreate | SurveyResponseUpdate) => {
      if (responseId) {
        return await updateSurveyResponse({ ...data, id: responseId })
      }
      return await createSurveyResponse(data)
    },
    onMutate: () => {
      setErrors([])
      setLoading(true)
    },

    onSuccess: async (data) => {
      if (!data?.errors?.length) {
        if (!isLast) {
          setInstance(data.instanceRef)
          handleResponseFormat(data.instanceRef.cursorRef?.currentQuestionRef?.responseRef?.value)
          setResponseId(data.instanceRef.cursorRef?.currentQuestionRef?.responseRef?.id)
          setResponseRef(data.instanceRef.cursorRef?.currentQuestionRef?.responseRef?.valueRef)
          if (setCurrentSection) {
            setCurrentSection(0)
          }
        } else {
          // Final answer saved — mark the instance completed so TRA/SIRE
          // submission is unblocked (survey_completed), then run the finish action.
          const finishedInstanceId = data.instanceRef?.id ?? instance?.id
          if (finishedInstanceId) {
            try {
              await completeSurveyInstance(finishedInstanceId)
            } catch (e) {
              // Rare: the instance can't be completed (e.g. template no longer
              // available). Don't lose the answers — finish anyway; the survey
              // stays incomplete and TRA/SIRE remains correctly blocked.
              console.error('Failed to mark survey instance complete', e)
            }
          }
          if (onFinishAction) {
            onFinishAction()
          }
        }
      } else {
        setErrors(data.errors)
      }
    },
    onSettled: () => {
      setLoading(false)
    },
  })

  const { mutate: previousQuestion } = useMutation({
    mutationFn: async (data: string) => {
      return await getPreviousQuestion(data)
    },
    onMutate: () => {
      setErrors([])
      setLoading(true)
    },
    onError: () => setLoading(false),
    onSuccess: async (data: SurveyInstance) => {
      setInstance(data)
      handleResponseFormat(data.cursorRef?.currentQuestionRef?.responseRef?.value)
      setResponseId(data.cursorRef?.currentQuestionRef?.responseRef?.id)
      setResponseRef(data.cursorRef?.currentQuestionRef?.responseRef?.valueRef)

      if (setCurrentSection) {
        setCurrentSection(0)
      }
      setLoading(false)
    },
  })

  const [choiceOptionPagination, _setChoiceOptionPagination] = useState(
    new Pagination({ size: 100 }),
  )
  const [choiceOptionFilters, setChoiceOptionFilters] = useState<Partial<ChoiceOptionFilter>>({
    search: '',
    question: currentQuestion?.id,
  })

  const handleSearch = useCallback(
    (search: string) => {
      setChoiceOptionFilters({
        search: search,
        question: currentQuestion?.id,
      })

      qc.invalidateQueries({ queryKey: ['question-options'] })
    },

    [setChoiceOptionFilters, currentQuestion?.id, qc],
  )

  useEffect(() => {
    if (!currentQuestion?.id) return
    if (choiceOptionFilters.question !== currentQuestion.id) {
      setChoiceOptionFilters(() => ({
        question: currentQuestion.id,
        search: '',
      }))
    }
  }, [choiceOptionFilters, currentQuestion?.id])

  const needsOptions = useMemo(() => {
    return (
      !!currentQuestion?.id &&
      !['OPEN_TEXT', 'DATE', 'NONE', null].includes(currentQuestion?.questionDisplay ?? '')
    )
  }, [currentQuestion])

  const { data: questionChoiceOptions, isLoading: loadingOptions } = useQuery({
    queryKey: ['question-options', choiceOptionFilters, needsOptions, choiceOptionPagination],
    queryFn: async () => {
      return await surveyQuestionCOapi.list({
        filters: choiceOptionFilters,
        pagination: choiceOptionPagination,
      })
    },
    enabled:
      needsOptions && !!currentQuestion?.id && choiceOptionFilters.question === currentQuestion.id,
  })

  useEffect(() => {
    if (existingInstance && !isSurveyInitialized.current) {
      isSurveyInitialized.current = true
      setInstanceId(existingInstance.id)
      setInstance(existingInstance as SurveyInstance)
      handleResponseFormat(existingInstance.cursorRef?.currentQuestionRef?.responseRef?.value)
      setResponseId(existingInstance.cursorRef?.currentQuestionRef?.responseRef?.id)
      setResponseRef(existingInstance.cursorRef?.currentQuestionRef?.responseRef?.valueRef)
      if (setCurrentSection) {
        setCurrentSection(0)
      }
    }
  }, [
    existingInstance,
    handleResponseFormat,
    setCurrentSection,
    setInstance,
    setInstanceId,
    setResponseRef,
  ])

  return {
    questionChoiceOptions,
    loadingOptions,
    needsOptions,
    previousQuestion,
    saveAndNext,
    existingInstance,
    loadingExistingInstance,
    createSurveyInstance,
    instance,
    instanceId,
    setInstanceId,
    currentQuestion,
    isFinished,
    setIsFinished,
    errors,
    loading,
    response,
    setResponse,
    responseId,
    choiceOptionFilters,
    choiceOptionPagination,
    setInstance,
    qc,
    isLast,
    handleSearch,
  }
}
